import { useState } from 'react';
import type { FormEvent } from 'react';
import { CheckCircle2, LoaderCircle, Receipt, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { ApiError } from '@/api/client';
import { useApproveClaim, useAdminClaims, useRejectClaim } from '@/api/hooks/useSuperadmin';
import type { PaymentClaim } from '@/api/payments';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { formatDateTime, money } from '@/lib/utils';

const STATUS_VARIANT = { Pending: 'low', Approved: 'ok', Rejected: 'out' } as const;

function ApproveDialog({ claim, onOpenChange }: { claim: PaymentClaim; onOpenChange: (open: boolean) => void }) {
  const approve = useApproveClaim();
  const [months, setMonths] = useState(String(claim.monthsRequested));
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const result = await approve.mutateAsync({
        id: claim.id,
        request: {
          monthsOverride: Number(months) !== claim.monthsRequested ? Math.max(1, Number(months) || 1) : undefined,
          note: note.trim() || undefined,
        },
      });
      toast({
        title: 'Claim approved',
        description: `Subscription extended to ${new Date(result.newSubscriptionEnd).toLocaleDateString('en-IN')}.`,
        variant: 'success',
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Approve claim</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground">
            {money(claim.amount)} via {claim.method} · {claim.pharmacySlug}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="approve-months">Months to extend</Label>
            <Input id="approve-months" type="number" min={1} max={60} value={months} onChange={(e) => setMonths(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="approve-note">Note (optional)</Label>
            <Input id="approve-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {error && (
            <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={approve.isPending}>
              {approve.isPending && <LoaderCircle className="animate-spin" />}
              Approve
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ claim, onOpenChange }: { claim: PaymentClaim; onOpenChange: (open: boolean) => void }) {
  const reject = useRejectClaim();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (note.trim().length < 3) {
      setError('A rejection note of 3–500 characters is required.');
      return;
    }
    try {
      await reject.mutateAsync({ id: claim.id, request: { note: note.trim() } });
      toast({ title: 'Claim rejected', variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Reject claim</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground">
            {money(claim.amount)} via {claim.method} · {claim.pharmacySlug}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="reject-note">Reason *</Label>
            <Input id="reject-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} required />
          </div>
          {error && (
            <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={reject.isPending}>
              {reject.isPending && <LoaderCircle className="animate-spin" />}
              Reject
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ClaimsTab() {
  const [params, setParams] = useSearchParams();
  const status = params.get('cstatus') ?? 'Pending';
  const page = Number(params.get('cpage') ?? '1');

  const [approveTarget, setApproveTarget] = useState<PaymentClaim | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PaymentClaim | null>(null);

  const { data, isLoading } = useAdminClaims({ status: status === 'all' ? undefined : status, page, limit: 25 });

  function updateParam(key: string, value: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'cpage') next.delete('cpage');
      return next;
    });
  }

  const claims = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={status} onValueChange={(v) => updateParam('cstatus', v)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">{data ? `${data.total} claim(s)` : 'Loading…'}</p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : claims.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <Receipt className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No claims here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {claims.map((claim) => (
            <Card key={claim.id} className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="tabular font-semibold">{money(claim.amount)}</p>
                    <Badge variant={STATUS_VARIANT[claim.status]}>{claim.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {claim.pharmacySlug} · {claim.method}
                    {claim.transactionRef && ` · UTR ${claim.transactionRef}`}
                    {claim.noReference && ' · no UTR'}
                    {' · '}
                    {claim.monthsRequested} month(s) · {formatDateTime(claim.createdAt)}
                  </p>
                  {claim.notes && <p className="mt-1 text-xs text-muted-foreground">"{claim.notes}"</p>}
                  {claim.reviewNote && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Reviewed by {claim.reviewedBy}: {claim.reviewNote}
                    </p>
                  )}
                </div>
                {claim.status === 'Pending' && (
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" variant="outline" onClick={() => setApproveTarget(claim)}>
                      <CheckCircle2 className="h-4 w-4" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-status-expired hover:bg-status-expired/10"
                      onClick={() => setRejectTarget(claim)}
                    >
                      <XCircle className="h-4 w-4" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => updateParam('cpage', String(page - 1))}>
            Previous
          </Button>
          <span className="tabular text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => updateParam('cpage', String(page + 1))}>
            Next
          </Button>
        </div>
      )}

      {approveTarget && <ApproveDialog claim={approveTarget} onOpenChange={(open) => !open && setApproveTarget(null)} />}
      {rejectTarget && <RejectDialog claim={rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)} />}
    </div>
  );
}
